import { CheckCircle2 } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Brand } from "../components/Brand";

const ERROR_MESSAGES: Record<string, string> = {
  forbidden:
    "Você não tem acesso ao OTG Tracker. Solicite acesso a um administrador do OTG.",
  oauth_cancelled: "Login cancelado.",
  invalid_state: "Sessão expirada. Tente novamente.",
  server_error: "Erro interno. Tente novamente.",
};

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const errorCode = searchParams.get("error");
  const errorMessage = errorCode
    ? (ERROR_MESSAGES[errorCode] ?? "Erro desconhecido.")
    : null;

  return (
    <div className="dark">
    <div className="min-h-screen min-h-dvh grid lg:grid-cols-2 bg-canvas">
      {/* Hero side */}
      <aside className="relative hidden lg:flex flex-col justify-between p-8 xl:p-12 text-ink border-r border-line overflow-hidden">
        {/* Gradient orbs */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-48 left-1/2 -translate-x-1/2 size-[600px] rounded-full bg-brand-500/[0.10] blur-[150px]" />
          <div className="absolute top-1/2 -translate-y-1/2 -left-32 size-[420px] rounded-full bg-brand-400/[0.07] blur-[130px]" />
          <div className="absolute -bottom-32 right-0 size-[500px] rounded-full bg-brand-600/[0.09] blur-[140px]" />
          <div className="absolute bottom-1/3 left-1/2 size-[280px] rounded-full bg-brand-500/[0.05] blur-[100px]" />
        </div>

        <Brand size="md" flat className="relative" />

        <div className="space-y-5 max-w-md">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1 text-xs font-medium text-ink-muted">
              UTMs padronizadas, sem fricção
            </div>
            <h1 className="text-3xl xl:text-4xl font-bold tracking-tight leading-[1.1]">
              Gere{" "}
              <span className="bg-gradient-to-r from-brand-500 to-brand-700 bg-clip-text text-transparent">
                links rastreáveis
              </span>{" "}
              com{" "}
              <span className="bg-gradient-to-r from-brand-500 to-brand-700 bg-clip-text text-transparent">
                padrão e visão 360
              </span>{" "}
              — da campanha ao clique.
            </h1>
            <p className="text-sm text-ink-muted leading-relaxed max-w-sm">
              Cole a URL, preencha os campos e copie o link encurtado. Sem
              planilha, sem dúvida de qual UTM usar, com rastreabilidade pronta
              para análise.
            </p>
          </div>

          <ul className="space-y-3">
            <Feature title="Multi-casas no mesmo padrão">
              Rode campanhas de diferentes casas em um único lugar com a mesma
              taxonomia.
            </Feature>
            <Feature title="Taxonomia consistente para todos">
              Campanha, canal, criativo e origem registrados sempre do mesmo
              jeito por toda a equipe.
            </Feature>
            <Feature title="Permissões configuráveis por usuário">
              Controle quem pode criar, editar ou aprovar campos e links.
            </Feature>
          </ul>
        </div>

        <p className="text-xs text-ink-subtle">
          © {new Date().getFullYear()} OTG Tracker · padronização de tráfego
        </p>
      </aside>

      {/* SSO side — dark purple background */}
      <div className="relative flex flex-col min-h-screen min-h-dvh lg:min-h-0 bg-brand-100 overflow-hidden">
        {/* Dot grid */}
        <div
          aria-hidden
          className="dot-grid pointer-events-none absolute inset-0"
        />
        {/* Ambient glow */}
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 right-0 size-[460px] rounded-full bg-brand-500/20 blur-[150px] motion-safe:animate-float-slow" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[500px] rounded-full bg-brand-500/[0.07] blur-[120px]" />
        </div>

        {/* Mobile brand header */}
        <div className="lg:hidden relative flex items-center px-5 pt-10 pb-5">
          <Brand size="md" />
        </div>

        {/* Form — natural flow on mobile, centered on desktop */}
        <div className="relative px-5 pb-5 lg:flex lg:flex-1 lg:items-center lg:justify-center lg:p-12">
          <div className="w-full lg:max-w-sm motion-safe:animate-slide-up">
            <div className="relative overflow-hidden rounded-2xl border border-white/[0.07] bg-surface/90 backdrop-blur-xl p-8 sm:p-9 shadow-[0_8px_40px_rgba(0,0,0,0.40),0_1px_0_rgba(255,255,255,0.05)]">
              <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-brand-600 via-brand-500 to-brand-400" />
              {/* Heading + description */}
              <div className="space-y-2 mb-7">
                <h2 className="text-[1.6rem] font-bold text-ink tracking-tight leading-tight">
                  Acessar painel
                </h2>
                <p className="text-sm text-ink-muted leading-relaxed">
                  Acesso restrito a contas{" "}
                  <span className="text-brand-700 font-medium">
                    @grupootg.com
                  </span>
                  . Faça login com sua conta Google corporativa para continuar.
                </p>
              </div>

              {/* Button + error */}
              <div className="space-y-3 animate-fade-in">
                {errorMessage && (
                  <div className="text-sm text-danger-ink bg-danger-soft/60 border border-danger/30 rounded-lg px-3.5 py-2.5 animate-fade-in">
                    {errorMessage}
                  </div>
                )}
                <a
                  href="/api/auth/login"
                  className="flex w-full items-center justify-center gap-3 rounded-xl bg-white px-5 py-3.5 text-[15px] font-semibold text-gray-800 shadow-[0_2px_12px_rgba(0,0,0,0.35),0_0_0_1px_rgba(255,255,255,0.08)] transition-all duration-200 hover:bg-gray-50 hover:shadow-[0_4px_20px_rgba(0,0,0,0.50)] hover:-translate-y-[1px] active:scale-[0.98] active:translate-y-0"
                >
                  <GoogleIcon />
                  Entrar com Google
                </a>
                <a
                  href="/api/auth/login?prompt=login"
                  className="block text-center text-xs text-ink-subtle hover:text-ink-muted transition-colors"
                >
                  Entrar com outra conta
                </a>
              </div>

              {/* Divider + footer note */}
              <div className="mt-5 pt-5 border-t border-white/[0.06]">
                <p className="text-xs text-ink-subtle text-center leading-relaxed">
                  Não tem acesso? Peça ao administrador para liberar seu email
                  no domínio corporativo.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile copy — hidden on desktop */}
        <div className="lg:hidden relative flex flex-col gap-6 px-5 pt-8 pb-12">
          {/* Top divider with glow */}
          <div className="absolute top-0 inset-x-5 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />

          <div className="inline-flex items-center gap-1.5 rounded-lg border border-brand-300/30 bg-brand-100/20 px-3 py-1 text-xs font-medium text-brand-700 w-fit">
            UTMs padronizadas, sem fricção
          </div>

          <h2 className="text-[1.75rem] font-bold tracking-tight leading-[1.15] text-ink">
            Gere{" "}
            <span className="bg-gradient-to-r from-brand-500 to-brand-700 bg-clip-text text-transparent">
              links rastreáveis
            </span>{" "}
            com{" "}
            <span className="bg-gradient-to-r from-brand-500 to-brand-700 bg-clip-text text-transparent">
              padrão, controle e visão 360
            </span>{" "}
            — da construção da campanha ao clique.
          </h2>

          <p className="text-sm text-ink-muted leading-relaxed -mt-1">
            Cole a URL da casa, preencha os campos obrigatórios e copie o link
            encurtado. Sem planilha, sem dúvida de qual UTM usar e com
            rastreabilidade pronta para análise.
          </p>

          <ul className="space-y-4">
            <Feature title="Multi-casas no mesmo padrão">
              Rode campanhas de diferentes casas em um único lugar, mantendo a
              mesma taxonomia.
            </Feature>
            <Feature title="Taxonomia consistente">
              Campanha, canal, criativo, influenciador e origem registrados
              sempre do mesmo jeito.
            </Feature>
            <Feature title="Permissões configuráveis">
              Controle quem pode criar, editar ou aprovar campos e links.
            </Feature>
          </ul>

          <p className="text-xs text-ink-subtle pt-2">
            © {new Date().getFullYear()} OTG Tracker · padronização de tráfego
          </p>
        </div>
      </div>
    </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.658 14.013 17.64 11.706 17.64 9.2z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}

function Feature({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="flex items-start gap-3">
      <CheckCircle2 className="size-5 text-brand-600 shrink-0 mt-0.5" />
      <div className="space-y-0.5">
        <p className="text-sm font-semibold text-ink">{title}</p>
        <p className="text-sm text-ink-muted leading-relaxed">{children}</p>
      </div>
    </li>
  );
}
